import { PIPELINE_STATUSES, type HotelListItem, type PipelineStatus } from "@carago/shared";
import { Link } from "react-router-dom";
import {
  formatBoolTri,
  formatContactSummary,
  formatDateTime,
  hotelDetailPath,
} from "../lib/hotels.js";

export type HotelsListViewState = "loading" | "error" | "empty" | "ready";

export type HotelsListViewProps = {
  state: HotelsListViewState;
  errorMessage?: string | null;
  hotels: HotelListItem[];
  q: string;
  pipelineStatus: PipelineStatus | "";
  hasNextPage: boolean;
  loadingMore: boolean;
  onQChange: (value: string) => void;
  onPipelineStatusChange: (value: PipelineStatus | "") => void;
  onLoadMore: () => void;
};

const STATUS_LABELS: Record<PipelineStatus, string> = {
  discovered: "Discovered",
  researched: "Researched",
  qualified: "Qualified",
  ready_for_outreach: "Ready for outreach",
  paused: "Paused",
  disqualified: "Disqualified",
};

/** Presentational pipeline list (testable via react-dom/server). */
export function HotelsListView({
  state,
  errorMessage,
  hotels,
  q,
  pipelineStatus,
  hasNextPage,
  loadingMore,
  onQChange,
  onPipelineStatusChange,
  onLoadMore,
}: HotelsListViewProps) {
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <label className="min-w-[14rem] flex-1">
          <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Suche
          </span>
          <input
            data-testid="hotels-search"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={q}
            onChange={(e) => onQChange(e.target.value)}
            placeholder="Name, Stadt, Land…"
          />
        </label>
        <label className="min-w-[12rem]">
          <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Pipeline-Status
          </span>
          <select
            data-testid="hotels-pipeline-filter"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={pipelineStatus}
            onChange={(e) =>
              onPipelineStatusChange(
                e.target.value as PipelineStatus | "",
              )
            }
          >
            <option value="">Alle</option>
            {PIPELINE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state === "loading" ? (
        <p data-testid="hotels-loading" className="text-sm text-gray-500">
          Lade Hotels…
        </p>
      ) : null}

      {state === "error" ? (
        <div
          data-testid="hotels-error"
          className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800"
        >
          {errorMessage ?? "Hotels konnten nicht geladen werden."}
        </div>
      ) : null}

      {state === "empty" ? (
        <div
          data-testid="hotels-empty"
          className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-600"
        >
          Keine Hotels für die aktuellen Filter.
        </div>
      ) : null}

      {state === "ready" ? (
        <div
          data-testid="hotels-table"
          className="overflow-x-auto rounded-xl border border-gray-200 bg-white"
        >
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-3">Hotel</th>
                <th className="px-3 py-3">Ort</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Score</th>
                <th className="px-3 py-3">Prio</th>
                <th className="px-3 py-3">Shuttle</th>
                <th className="px-3 py-3">Transfer</th>
                <th className="px-3 py-3">Kontakt</th>
                <th className="px-3 py-3">Recherche</th>
                <th className="px-3 py-3">Nächste Aktion</th>
                <th className="px-3 py-3">Fällig</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {hotels.map((hotel) => (
                <tr key={hotel.id} className="hover:bg-gray-50/80">
                  <td className="px-3 py-3">
                    <Link
                      data-testid={`hotel-link-${hotel.id}`}
                      to={hotelDetailPath(hotel.id)}
                      className="font-medium text-violet-700 hover:underline"
                    >
                      {hotel.name}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-gray-600">
                    {[hotel.city, hotel.country].filter(Boolean).join(", ") ||
                      "—"}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {STATUS_LABELS[hotel.pipeline_status]}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {hotel.lead_score ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {hotel.priority ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {formatBoolTri(hotel.has_shuttle)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {formatBoolTri(hotel.has_airport_transfer)}
                  </td>
                  <td className="px-3 py-3 text-gray-600">
                    {formatContactSummary(hotel.primary_contact)}
                  </td>
                  <td className="px-3 py-3 text-gray-500">
                    {formatDateTime(hotel.last_researched_at)}
                  </td>
                  <td className="px-3 py-3 text-gray-700">
                    {hotel.next_action ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-gray-500">
                    {formatDateTime(hotel.next_action_due_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {state === "ready" && hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            data-testid="hotels-load-more"
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            {loadingMore ? "Lade…" : "Mehr laden"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
